import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { getBlockedUsers, unblockUser } from "../../utils/discoverApi";

const BODY = "#0f172a";
const SUBTEXT = "#334155";
const BORDER = "#e2e8f0";

function displayNameFor(entry) {
  const fullName = `${entry.first_name || ""} ${entry.last_name || ""}`.trim();
  return fullName || entry.username || "User";
}

export default function BlockedAccountsSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getBlockedUsers();
      setBlocked(Array.isArray(data) ? data : []);
      setLoaded(true);
    } catch (err) {
      setError(err?.message || "Couldn't load your blocked accounts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    if (location.hash === "#blocked-accounts") {
      navigate({ pathname: location.pathname, hash: "" }, { replace: true });
    }
  };

  useEffect(() => {
    if (location.hash === "#blocked-accounts") {
      setOpen(true);
    }
  }, [location.hash]);

  useEffect(() => {
    if (open && !loaded && !loading) {
      load();
    }
  }, [open, loaded, loading, load]);

  const handleUnblock = async (entry) => {
    setPendingId(entry.id);
    setError("");
    try {
      await unblockUser(entry.id);
      setBlocked((prev) => prev.filter((item) => item.id !== entry.id));
    } catch (err) {
      setError(err?.message || "Couldn't unblock this account. Please try again.");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <>
      <Box id="blocked-accounts" sx={{ scrollMarginTop: "88px" }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ fontWeight: 700, color: BODY, mb: 2, fontSize: { xs: 20, sm: 22 } }}
        >
          Blocked accounts
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleOpen}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            px: 2,
            textTransform: "none",
            color: SUBTEXT,
            borderColor: BORDER,
            borderRadius: "12px",
            bgcolor: "#fff",
            "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <BlockIcon sx={{ color: "#64748b", fontSize: 20, flexShrink: 0 }} />
            <Box sx={{ textAlign: "left", minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: BODY, fontSize: 15 }}>
                Manage blocked accounts
              </Typography>
              <Typography sx={{ fontSize: 13, color: SUBTEXT, lineHeight: 1.4 }}>
                Hidden posts and comments from people you&apos;ve blocked
              </Typography>
            </Box>
          </Box>
          <ChevronRightIcon sx={{ color: "#94a3b8", flexShrink: 0 }} />
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        aria-labelledby="blocked-accounts-title"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: BODY }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <DialogTitle
          id="blocked-accounts-title"
          sx={{ pt: 2.5, pb: 0.5, px: 3, pr: 5 }}
        >
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: 18, color: BODY }}>
            Blocked accounts
          </Typography>
          <Typography sx={{ fontSize: 14, color: SUBTEXT, mt: 0.75, lineHeight: 1.5, fontWeight: 400 }}>
            Unblocking does not restore follows.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: "#047857" }} />
            </Box>
          ) : blocked.length === 0 ? (
            <Typography sx={{ fontSize: 15, color: SUBTEXT, py: 2, textAlign: "center" }}>
              You haven&apos;t blocked anyone.
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
              {blocked.map((entry, index) => {
                const name = displayNameFor(entry);
                return (
                  <Box
                    component="li"
                    key={entry.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1.25,
                      borderTop: index > 0 ? `1px solid ${BORDER}` : "none",
                    }}
                  >
                    <Avatar
                      src={entry.profile_image_url || undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "#e2e8f0",
                        color: BODY,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        component={RouterLink}
                        to={`/profile/${entry.id}`}
                        onClick={handleClose}
                        sx={{
                          display: "block",
                          fontWeight: 600,
                          fontSize: 15,
                          color: BODY,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        noWrap
                      >
                        {name}
                      </Typography>
                      {entry.username && (
                        <Typography sx={{ fontSize: 13, color: SUBTEXT }} noWrap>
                          @{entry.username}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="text"
                      onClick={() => handleUnblock(entry)}
                      disabled={pendingId === entry.id}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 14,
                        color: BODY,
                        minWidth: 72,
                        flexShrink: 0,
                        "&:hover": { bgcolor: "rgba(15, 23, 42, 0.04)" },
                      }}
                    >
                      {pendingId === entry.id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        "Unblock"
                      )}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
